// pms-adapters / rows — snake_case DB row shapes + row→domain mappers.
//
// This is the ONLY place that knows the DB column names. It moved here out of
// apps/admin/src/pms/logic.ts (SoC: row mapping is an infra concern, not app
// logic). Mappers produce @lemontaigu/pms-core domain types; casts on the enum
// columns are the single trusted boundary where a DB string becomes a union.

import type {
  BookingSearchHit,
  BookingSearchResult,
  ChildMealBand,
  SoldTerms,
  PmsBoard,
  PmsGuarantee,
  PmsReservationStatus,
  PmsSource,
  Rate,
  StoredRateParameterSet,
} from '@lemontaigu/pms-core';
import type {
  AppliesTo,
  AvailabilityCell,
  ChannexRatePlanLink,
  Charge,
  ChargeDay,
  Folio,
  OccupancySpan,
  ChargeOrigin,
  AgeBand,
  MealPrestation,
  StayPerson,
  StayPersonMeal,
  StayPersonNeeds,
  Allergen,
  OtaService,
  ChargeKind,
  CustomerSubtype,
  CustomerType,
  Guest,
  GuestSheet,
  GuestSummary,
  Product,
  GuestLang,
  RatePlan,
  Reservation,
  ReservationEvent,
  ReservationView,
  BedConfig,
  BedType,
  Room,
  RoomBed,
  RoomBlock,
  RoomStayView,
  RoomType,
} from '@lemontaigu/pms-core';

// ── Row shapes ───────────────────────────────────────────────────────────────

export interface RoomTypeRow {
  id: number;
  property_code: string;
  code: string;
  label_fr: string;
  max_occupancy: number;
  channex_room_type_id: string | null;
}

export interface BedConfigRow {
  code: string;
  label_fr: string;
  doubles: number;
  singles: number;
  inferred: boolean;
  sort_order: number;
  active: boolean;
}

/** `pms_bed_type` (0173) — the furniture, separate from the arrangement. */
export interface BedTypeRow {
  code: string;
  label_fr: string;
  sleeps: number;
  sort_order: number;
  active: boolean;
}

/** `pms_room_config_option` (0173) — one row per arrangement a room can take. */
export interface RoomConfigOptionRow {
  room_id: number;
  bed_config: string;
}

export interface RoomRow {
  id: number;
  property_code: string;
  room_number: string;
  floor: number;
  room_type_id: number | null;
  hk_status: string;
  current_bed_config: string | null;
  current_bed_config_source: string | null;
  current_bed_config_at: string | null;
  double_bed_type: string | null;
  updated_by: string | null;
  updated_at: string | null;
  max_occupancy: number | null;
  is_superior: boolean;
  heating_zone: number | null;
  bathroom_heating_zone: number | null;
  room_view: string | null;
  has_shower: boolean;
  wc_not_separated: boolean;
  remarks: string | null;
  // The unit model (P1.6). NULL scalars mean "no opinion", never zero.
  unit_kind: string;
  bedrooms: number | null;
  living_rooms: number | null;
  bathrooms: number | null;
  wc_count: number | null;
  kitchen: string | null;
  surface_sqm: number | string | null;
}

export interface RoomBedRow {
  room_id: number;
  space_kind: string;
  space_index: number;
  bed_config: string;
  quantity: number;
}

export interface RatePlanRow {
  id: number;
  property_code: string;
  code: string;
  label_fr: string;
  board: string;
  /** PB7a (20261026120100) — optional so a select that omits it still maps. */
  cancellation_policy_id?: number | null;
  distributed: boolean;
  channel_code: string | null;
  derives_from_id: number | null;
}

/** `pms_rate_parameter_set` (0107). `parameters` is the whole domain object as
 *  one jsonb document — see the migration header for why it is not five tables. */
export interface RateParameterSetRow {
  id: number;
  name: string;
  note: string | null;
  status: string;
  parameters: Record<string, unknown>;
  published_at: string | null;
}

export interface GuestRow {
  id: string;
  last_name: string;
  first_name: string;
  email: string | null;
  phone: string | null;
  lang: string;
  notes: string | null;
}

/** A row of `pms_guest_search` (20260929100100). */
export interface GuestSearchRow {
  id: string;
  last_name: string;
  first_name: string;
  email: string | null;
  phone: string | null;
  mobile: string | null;
  city: string | null;
  country_code: string | null;
  lang: string;
  customer_type: string;
  customer_subtype: string | null;
  siren: string | null;
  source_vega_ref: string | null;
  cardex: boolean;
  vega_held: boolean;
  cardex_pass: number | null;
  cardex_last_activity: string | null;
}

/** The row of `pms_guest_sheet`. */
export interface GuestSheetRow extends Omit<GuestSearchRow, 'cardex'> {
  address_line1: string | null;
  address_line2: string | null;
  postal_code: string | null;
  notes: string | null;
  vat_number: string | null;
  cardex_loaded_at: string | null;
  updated_at: string;
}

export interface CustomerSubtypeRow {
  code: string;
  label_fr: string;
  active: boolean;
  sort_order: number;
}

export interface ReservationRow {
  id: string;
  property_code: string;
  status: string;
  no_show: boolean;
  guest_id: string;
  room_type_id: number;
  room_id: number | null;
  checkin_date: string;
  checkout_date: string;
  adults: number;
  children: number;
  source: string;
  channel: string | null;
  channel_ref: string | null;
  booking_party_code: string | null;
  /** PR1 (`20261012120100`) — optional so a row read before it maps to an individual. */
  kind?: string | null;
  group_name?: string | null;
  cancellation_reason_code: string | null;
  channex_booking_id: string | null;
  guarantee: string;
  card_vault_ref: string | null;
  vad_amount_cents: number | null;
  /** `20261101100100` — arrhes by transfer: the day it is due. Optional so a row read before that migration maps to null. */
  transfer_due_on?: string | null;
  total_cents: number;
  notes: string | null;
  /** `20260929040100` — optional so a row read before that migration maps to
   *  null rather than to undefined. */
  source_text?: string | null;
  arrival_window_from?: string | null;
  arrival_window_to?: string | null;
  source_vega_resa?: string | null;
  created_at: string;
  updated_at: string;
  pms_guest?: GuestRow | null;
  pms_room?: { room_number: string } | null;
}

export interface ReservationEventRow {
  id: number;
  reservation_id: string;
  event: string;
  payload: Record<string, unknown>;
  actor: string | null;
  created_at: string;
  /** The computed field `operator_label(pms_reservation_event)` (ADR-0026). */
  operator_label?: string | null;
}

export interface RoomBlockRow {
  id: number;
  room_id: number;
  start_date: string;
  end_date: string;
  reason: string | null;
}

export interface AvailabilityRow {
  day: string;
  room_type_id: number;
  code: string;
  total: number;
  occupied: number;
  blocked: number;
  available: number;
}

// ── Mappers ──────────────────────────────────────────────────────────────────

export const rowToRoomType = (r: RoomTypeRow): RoomType => ({
  propertyCode: r.property_code,
  id: r.id,
  code: r.code,
  labelFr: r.label_fr,
  maxOccupancy: r.max_occupancy,
  channexRoomTypeId: r.channex_room_type_id,
});

export const rowToBedConfig = (r: BedConfigRow): BedConfig => ({
  code: r.code,
  labelFr: r.label_fr,
  sortOrder: r.sort_order,
  active: r.active,
  layout: {
    doubles: r.doubles,
    singles: r.singles,
    inferred: r.inferred,
  },
});

export const rowToBedType = (r: BedTypeRow): BedType => ({
  code: r.code,
  labelFr: r.label_fr,
  sleeps: r.sleeps,
  sortOrder: r.sort_order,
  active: r.active,
});

/** ⚠ `options` is a SEPARATE argument because the achievable set lives in its
 *  own table (`pms_room_config_option`, 0173) — there is no column on the room
 *  to read it from any more. A caller that forgets it gets an empty set, which
 *  `pmsIsMultiSpace` reads as « this is an apartment »; the repository builds
 *  the map once per fetch so no call site can omit it by accident. */
export const rowToRoom = (r: RoomRow, options: readonly string[] = []): Room => ({
  propertyCode: r.property_code,
  id: r.id,
  roomNumber: r.room_number,
  floor: r.floor,
  roomTypeId: r.room_type_id,
  hkStatus: (r.hk_status as Room['hkStatus']) ?? 'clean',
  configOptions: options,
  currentBedConfig: r.current_bed_config,
  currentBedConfigSource: (r.current_bed_config_source as Room['currentBedConfigSource']) ?? null,
  currentBedConfigAt: r.current_bed_config_at,
  doubleBedType: r.double_bed_type,
  updatedBy: r.updated_by,
  updatedAt: r.updated_at,
  maxOccupancy: r.max_occupancy,
  isSuperior: r.is_superior,
  heatingZone: r.heating_zone,
  bathroomHeatingZone: r.bathroom_heating_zone,
  view: r.room_view,
  hasShower: r.has_shower,
  wcNotSeparated: r.wc_not_separated,
  remarks: r.remarks,
  unitKind: (r.unit_kind as Room['unitKind']) ?? 'room',
  bedrooms: r.bedrooms,
  livingRooms: r.living_rooms,
  bathrooms: r.bathrooms,
  wcCount: r.wc_count,
  kitchen: (r.kitchen as Room['kitchen']) ?? null,
  // numeric(6,2) arrives as a STRING over PostgREST, not a number — the same
  // trap money columns have. Coerce here, in the mapper, so nothing downstream
  // ever compares a string to a number and silently disagrees.
  surfaceSqm: r.surface_sqm === null ? null : Number(r.surface_sqm),
});

export const rowToRoomBed = (r: RoomBedRow): RoomBed => ({
  roomId: r.room_id,
  spaceKind: r.space_kind as RoomBed['spaceKind'],
  spaceIndex: r.space_index,
  bedConfig: r.bed_config,
  quantity: r.quantity,
});

export const rowToRatePlan = (r: RatePlanRow): RatePlan => ({
  propertyCode: r.property_code,
  id: r.id,
  code: r.code,
  labelFr: r.label_fr,
  board: r.board as PmsBoard,
  cancellationPolicyId: r.cancellation_policy_id ?? null,
  // `?? true` is for the window between this code deploying and 0108 applying,
  // and for nothing else: the column is NOT NULL with a default, so a row that
  // reaches here without it came from a database that predates the migration.
  // Defaulting to `true` reproduces the pre-0108 behaviour exactly rather than
  // silently withholding every plan from the channel.
  distributed: r.distributed ?? true,
  channelCode: r.channel_code ?? null,
  derivesFromId: r.derives_from_id ?? null,
});

export const rowToRateParameterSet = (r: RateParameterSetRow): StoredRateParameterSet => ({
  id: r.id,
  status: r.status as StoredRateParameterSet['status'],
  publishedAt: r.published_at,
  // The row's `name`/`note` columns are the SET's own fields, lifted back into
  // the document. They are stored as columns as well as inside the jsonb so the
  // database can enforce uniqueness and the freeze on them — the document alone
  // could not be indexed or constrained. This mapper is the one place that
  // reconciles the two, and the column is authoritative: it is what the unique
  // index and the guard trigger actually see.
  parameters: {
    ...(r.parameters as unknown as StoredRateParameterSet['parameters']),
    name: r.name,
    note: r.note,
  },
});

/** `pms_rate` — the ARI grid row. */
export interface RateRow {
  rate_plan_id: number;
  room_type_id: number;
  date: string;
  price_cents: number;
  min_stay: number;
  closed: boolean;
  hand_adjusted?: boolean;
  /** CC1 — absent on a database the CC1 migration has not reached yet. */
  closed_to_arrival?: boolean;
  closed_to_departure?: boolean;
  max_stay?: number | null;
}

export const rowToRate = (r: RateRow): Rate => ({
  ratePlanId: r.rate_plan_id,
  roomTypeId: r.room_type_id,
  date: r.date,
  priceCents: r.price_cents,
  closed: r.closed,
  minStay: r.min_stay,
  // `?? true` for the window between this code deploying and 0117 applying,
  // and the direction matters: a row read from a database without the column
  // is presumed HAND-ADJUSTED, which is the same asymmetry 0117's backfill
  // reasons from — skipping a derived row is reported, destroying a typed one
  // is silent. (`rowToRatePlan` above defaults the other way for `distributed`
  // because there the safe direction is « as it behaved before ».)
  handAdjusted: r.hand_adjusted ?? true,
  ...rateRestrictionFields(r),
});

/**
 * CC1's three columns, carried ONLY when the row has them. A row read from a
 * database the CC1 migration has not reached leaves the fields absent, so the
 * hand write path (`upsertRates`) does not send them back as columns that do
 * not exist there — production's database is migrated by hand, after the code
 * that reads it has already deployed (RUNBOOK §2b).
 */
export function rateRestrictionFields(
  r: Pick<RateRow, 'closed_to_arrival' | 'closed_to_departure' | 'max_stay'>,
): Pick<Rate, 'closedToArrival' | 'closedToDeparture' | 'maxStay'> {
  return {
    ...(r.closed_to_arrival === undefined ? {} : { closedToArrival: r.closed_to_arrival }),
    ...(r.closed_to_departure === undefined ? {} : { closedToDeparture: r.closed_to_departure }),
    ...(r.max_stay === undefined ? {} : { maxStay: r.max_stay }),
  };
}

/** The write side of `rateRestrictionFields`: a field the Rate does not carry
 *  is not written, so an edit made before CC1 reached the database still saves. */
export function rateRestrictionColumns(
  x: Pick<Rate, 'closedToArrival' | 'closedToDeparture' | 'maxStay'>,
): Pick<RateRow, 'closed_to_arrival' | 'closed_to_departure' | 'max_stay'> {
  return {
    ...(x.closedToArrival === undefined ? {} : { closed_to_arrival: x.closedToArrival }),
    ...(x.closedToDeparture === undefined ? {} : { closed_to_departure: x.closedToDeparture }),
    ...(x.maxStay === undefined ? {} : { max_stay: x.maxStay }),
  };
}

export const rowToGuest = (r: GuestRow): Guest => ({
  id: r.id,
  lastName: r.last_name,
  firstName: r.first_name,
  email: r.email,
  phone: r.phone,
  lang: r.lang as GuestLang,
  notes: r.notes,
});

export const rowToGuestSummary = (r: GuestSearchRow): GuestSummary => ({
  id: r.id,
  lastName: r.last_name,
  firstName: r.first_name,
  email: r.email,
  phone: r.phone,
  mobile: r.mobile,
  city: r.city,
  countryCode: r.country_code,
  lang: r.lang as GuestLang,
  customerType: r.customer_type as CustomerType,
  customerSubtype: r.customer_subtype,
  siren: r.siren,
  sourceVegaRef: r.source_vega_ref,
  cardex: r.cardex,
  vegaHeld: r.vega_held,
  cardexPass: r.cardex_pass === 1 || r.cardex_pass === 2 ? r.cardex_pass : null,
  cardexLastActivity: r.cardex_last_activity,
});

export const rowToGuestSheet = (r: GuestSheetRow): GuestSheet => ({
  ...rowToGuestSummary({ ...r, cardex: r.cardex_loaded_at !== null }),
  addressLine1: r.address_line1,
  addressLine2: r.address_line2,
  postalCode: r.postal_code,
  notes: r.notes,
  vatNumber: r.vat_number,
  cardexLoadedAt: r.cardex_loaded_at,
  updatedAt: r.updated_at,
});

export const rowToCustomerSubtype = (r: CustomerSubtypeRow): CustomerSubtype => ({
  code: r.code,
  labelFr: r.label_fr,
  active: r.active,
  sortOrder: r.sort_order,
});

const baseReservation = (r: ReservationRow): Reservation => ({
  id: r.id,
  status: r.status as PmsReservationStatus,
  noShow: r.no_show,
  guestId: r.guest_id,
  roomTypeId: r.room_type_id,
  roomId: r.room_id,
  checkinDate: r.checkin_date,
  checkoutDate: r.checkout_date,
  adults: r.adults,
  children: r.children,
  source: r.source as PmsSource,
  channel: r.channel,
  channelRef: r.channel_ref,
  bookingPartyCode: r.booking_party_code ?? null,
  kind: r.kind === 'group' ? 'group' : 'individual',
  groupName: r.group_name ?? null,
  cancellationReasonCode: r.cancellation_reason_code ?? null,
  channexBookingId: r.channex_booking_id,
  guarantee: r.guarantee as PmsGuarantee,
  cardVaultRef: r.card_vault_ref,
  vadAmountCents: r.vad_amount_cents,
  transferDueOn: r.transfer_due_on ?? null,
  totalCents: r.total_cents,
  notes: r.notes,
  sourceText: r.source_text ?? null,
  arrivalWindowFrom: r.arrival_window_from ?? null,
  arrivalWindowTo: r.arrival_window_to ?? null,
  sourceVegaResa: r.source_vega_resa ?? null,
  createdAt: r.created_at,
  updatedAt: r.updated_at,
});

export const rowToReservationView = (r: ReservationRow): ReservationView => ({
  ...baseReservation(r),
  propertyCode: r.property_code,
  guest: r.pms_guest ? rowToGuest(r.pms_guest) : null,
  roomNumber: r.pms_room?.room_number ?? null,
});

export const rowToReservationEvent = (r: ReservationEventRow): ReservationEvent => ({
  id: r.id,
  reservationId: r.reservation_id,
  event: r.event,
  payload: r.payload,
  actor: r.actor,
  operatorLabel: r.operator_label ?? null,
  createdAt: r.created_at,
});

export const rowToRoomBlock = (r: RoomBlockRow): RoomBlock => ({
  id: r.id,
  roomId: r.room_id,
  startDate: r.start_date,
  endDate: r.end_date,
  reason: r.reason,
});

export const rowToAvailabilityCell = (r: AvailabilityRow): AvailabilityCell => ({
  day: r.day,
  roomTypeId: r.room_type_id,
  code: r.code,
  total: r.total,
  occupied: r.occupied,
  blocked: r.blocked,
  available: r.available,
});


// ── Room stays — « les logements » (multi-room brief, migrations 0086-0091) ──

export interface RoomStayRow {
  id: string;
  property_code: string;
  reservation_id: string;
  status: string;
  room_type_id: number;
  /** 20261022100100 — the category the logement was sold as; absent from an older database. */
  sold_room_type_id?: number | null;
  /** PB7b (20261026130100) — optional so a select that omits it still maps. */
  cancellation_terms?: SoldTerms | null;
  /** The Vega stay a seeded logement came from (VEGA-SEED-BRIEF §4); absent from the rack window's rows. */
  source_vega_stay_key?: string | null;
  room_id: number | null;
  rate_plan_id: number;
  checkin_date: string;
  checkout_date: string;
  occupant_name: string | null;
  adults: number;
  children: number;
  infants: number;
  /** 0136 — optional for the same reason as `source_text` above. */
  requested_bed_config?: string | null;
  cancelled_at: string | null;
  ota_unique_id: string | null;
  /** Group edit step 2 — absent from the rack window's rows. */
  meals?: string[] | null;
  /** Present only on the embedded read (`pms_room!...(room_number)`). */
  pms_room?: { room_number: string } | { room_number: string }[] | null;
}

/** Both shapes accepted on purpose. The reservation read above can type its
 *  embed as a bare object because it joins on a plain FK; a room stay reaches
 *  `pms_room` through the COMPOSITE (property_code, room_id) FK, and PostgREST
 *  is not reliably to-one across those. Verified against the running stack
 *  (it returns an object today) — the array arm is the cheap insurance, not a
 *  claim about what happened. */
const embeddedRoomNumber = (e: RoomStayRow['pms_room']): string | null => {
  if (e === null || e === undefined) return null;
  const one = Array.isArray(e) ? e[0] : e;
  return one?.room_number ?? null;
};

export const rowToRoomStay = (r: RoomStayRow): RoomStayView => ({
  propertyCode: r.property_code,
  id: r.id,
  reservationId: r.reservation_id,
  status: r.status as PmsReservationStatus,
  roomTypeId: r.room_type_id,
  soldRoomTypeId: r.sold_room_type_id ?? null,
  cancellationTerms: r.cancellation_terms ?? null,
  ...(r.source_vega_stay_key === undefined ? {} : { fromVega: r.source_vega_stay_key !== null }),
  roomId: r.room_id,
  ratePlanId: r.rate_plan_id,
  checkinDate: r.checkin_date,
  checkoutDate: r.checkout_date,
  occupantName: r.occupant_name,
  adults: r.adults,
  children: r.children,
  infants: r.infants,
  requestedBedConfig: r.requested_bed_config ?? null,
  cancelledAt: r.cancelled_at,
  otaUniqueId: r.ota_unique_id,
  ...(r.meals === undefined || r.meals === null ? {} : { meals: r.meals as MealPrestation[] }),
  roomNumber: embeddedRoomNumber(r.pms_room),
});

// ── The planning's search box (pms_reservation_search) ─────────────────────

/** The RPC's jsonb, verbatim (already camelCase: built by the function). */
export interface BookingSearchJson {
  hits?: Array<{
    reservationId?: unknown;
    propertyCode?: unknown;
    number?: unknown;
    status?: unknown;
    guestName?: unknown;
    groupName?: unknown;
    channel?: unknown;
    channelRef?: unknown;
    checkinDate?: unknown;
    checkoutDate?: unknown;
    rooms?: unknown;
    roomCount?: unknown;
    stayId?: unknown;
  }>;
  more?: unknown;
}

const strOrNull = (v: unknown): string | null => (typeof v === 'string' && v !== '' ? v : null);

/** Maps the reply. null when it is not a reply this build can read — a hit
 *  without an id or dates cannot be reached, and a half-read list must not
 *  pass for « these are all the bookings ». */
export function toBookingSearch(j: BookingSearchJson | null): BookingSearchResult | null {
  if (!j || typeof j !== 'object' || !Array.isArray(j.hits)) return null;
  const hits: BookingSearchHit[] = [];
  for (const h of j.hits) {
    if (
      typeof h?.reservationId !== 'string' ||
      typeof h.propertyCode !== 'string' ||
      typeof h.status !== 'string' ||
      typeof h.checkinDate !== 'string' ||
      typeof h.checkoutDate !== 'string'
    ) {
      return null;
    }
    hits.push({
      reservationId: h.reservationId,
      propertyCode: h.propertyCode,
      number: strOrNull(h.number),
      status: h.status as PmsReservationStatus,
      guestName: strOrNull(h.guestName),
      groupName: strOrNull(h.groupName),
      channel: strOrNull(h.channel),
      channelRef: strOrNull(h.channelRef),
      checkinDate: h.checkinDate,
      checkoutDate: h.checkoutDate,
      rooms: Array.isArray(h.rooms) ? h.rooms.filter((x): x is string => typeof x === 'string') : [],
      roomCount: typeof h.roomCount === 'number' ? h.roomCount : 0,
      stayId: strOrNull(h.stayId),
    });
  }
  return { hits, more: j.more === true };
}

// ── Persons and their meals (group edit steps 1–2) ──────────────────────────

export interface StayPersonRow {
  id: string;
  room_stay_id: string;
  position: number;
  name: string | null;
  age_band: string;
  start_date: string;
  end_date: string;
}

export const rowToStayPerson = (r: StayPersonRow): StayPerson => ({
  id: r.id,
  roomStayId: r.room_stay_id,
  position: r.position,
  name: r.name,
  ageBand: r.age_band as AgeBand,
  startDate: r.start_date,
  endDate: r.end_date,
});

export interface StayPersonMealRow {
  person_id: string;
  service_date: string;
  prestation: string;
  taken: boolean;
}

export const rowToStayPersonMeal = (r: StayPersonMealRow): StayPersonMeal => ({
  personId: r.person_id,
  serviceDate: r.service_date,
  prestation: r.prestation as MealPrestation,
  taken: r.taken,
});

/** `pms_stay_person_needs` (group edit step 5) — health data. */
export interface StayPersonNeedsRow {
  person_id: string;
  needs: string | null;
  allergens: string[];
}

export const rowToStayPersonNeeds = (r: StayPersonNeedsRow): StayPersonNeeds => ({
  personId: r.person_id,
  needs: r.needs,
  allergens: r.allergens as Allergen[],
});

// ── Charges & products (charge brief §4, migrations 0082-0084) ───────────────

export interface ProductRow {
  id: number;
  property_code: string;
  code: string;
  label_fr: string;
  description_fr: string | null;
  kind: string;
  emoji: string | null;
  applies_to: string;
  per_person: boolean;
  price_cents: number;
  /** numeric(5,2). PostgREST returns numerics as STRINGS by default to avoid
   *  the float rounding that would make 5.50 arrive as 5.499999 — so the
   *  mapper parses rather than casts, and a cast here would compile while
   *  producing a string wearing a number's type. */
  tva_rate: string | number | null;
  is_tax: boolean;
  auto_apply: boolean;
  manual_qty: boolean;
  active: boolean;
  sort_order: number;
  /** PR3 (20261013100100) — optional so a select that omits it still maps. */
  board_prestation?: string | null;
  is_passthrough?: boolean;
  /** PB1 (20261026100100) — optional for the same reason. */
  profile_band?: string | null;
}

export interface ChannexRatePlanRow {
  rate_plan_id: number;
  room_type_id: number;
  channex_rate_plan_id: string;
  /** occupancy (as a JSON key, therefore text) -> Channex option UUID (0104). */
  channex_options: Record<string, string> | null;
}

export interface ChargeRow {
  id: number;
  reservation_id: string;
  room_stay_id: string | null;
  folio_id: number;
  kind: string;
  product_id: number | null;
  rate_plan_id: number | null;
  label_fr: string;
  quantity: number;
  unit_cents: number;
  start_date: string;
  end_date: string;
  tva_rate: string | number | null;
  persons_taxable: number | null;
  persons_exempt: number | null;
  is_manual: boolean;
  voided_at: string | null;
}

export interface FolioRow {
  id: number;
  reservation_id: string;
  room_stay_id: string | null;
  label_fr: string;
  payer_guest_id: string | null;
  payer_name: string | null;
}

export interface OccupancyRow {
  id: number;
  room_stay_id: string;
  start_date: string;
  end_date: string;
  adults: number;
  children: number;
  infants: number;
}

export interface OtaServiceRow {
  id: number;
  room_stay_id: string;
  name: string;
  price_mode: string;
  persons: number | null;
  nights: number | null;
  total_cents: number;
  product_id: number | null;
}

export interface ChargeDayRow {
  id: number;
  charge_id: number;
  reservation_id: string;
  room_stay_id: string | null;
  date: string;
  kind: string;
  product_id: number | null;
  rate_plan_id: number | null;
  label_fr: string;
  quantity: number;
  unit_cents: number;
  amount_cents: number;
  tva_rate: string | number | null;
  persons_taxable: number | null;
  persons_exempt: number | null;
  is_manual: boolean;
  voided_at: string | null;
  /** Embedded on the form's read only (`pms_charge!…(origin)`). */
  pms_charge?: { origin: string } | { origin: string }[] | null;
}

/** numeric → number, keeping null distinct from 0: on a charge line NULL means
 *  « bears no TVA » (a pass-through levy) and 0 would mean « taxed at zero ».
 *  They are different claims and an accountant reads them differently. */
const numericOrNull = (v: string | number | null): number | null =>
  v === null || v === undefined ? null : typeof v === 'number' ? v : Number(v);

export const rowToProduct = (r: ProductRow): Product => ({
  propertyCode: r.property_code,
  id: r.id,
  code: r.code,
  labelFr: r.label_fr,
  descriptionFr: r.description_fr,
  kind: r.kind as Product['kind'],
  emoji: r.emoji,
  appliesTo: r.applies_to as AppliesTo,
  perPerson: r.per_person,
  priceCents: r.price_cents,
  tvaRate: numericOrNull(r.tva_rate),
  isTax: r.is_tax,
  autoApply: r.auto_apply,
  manualQty: r.manual_qty,
  active: r.active,
  sortOrder: r.sort_order,
  boardPrestation: (r.board_prestation ?? null) as MealPrestation | null,
  isPassthrough: r.is_passthrough ?? false,
  profileBand: (r.profile_band ?? null) as ChildMealBand | null,
});

export const rowToChannexRatePlanLink = (r: ChannexRatePlanRow): ChannexRatePlanLink => ({
  ratePlanId: r.rate_plan_id,
  roomTypeId: r.room_type_id,
  channexRatePlanId: r.channex_rate_plan_id,
  // Ascending by occupancy: jsonb object key order is not ours to rely on, and
  // the push must not vary in shape run to run for a reason nobody can see.
  occupancyOptions: Object.entries(r.channex_options ?? {})
    .map(([occupancy, channexOptionId]) => ({ occupancy: Number(occupancy), channexOptionId }))
    .filter((o) => Number.isInteger(o.occupancy) && o.occupancy >= 1)
    .sort((a, b) => a.occupancy - b.occupancy),
});

export const rowToCharge = (r: ChargeRow): Charge => ({
  id: r.id,
  reservationId: r.reservation_id,
  roomStayId: r.room_stay_id,
  folioId: r.folio_id,
  kind: r.kind as ChargeKind,
  productId: r.product_id,
  ratePlanId: r.rate_plan_id,
  labelFr: r.label_fr,
  quantity: r.quantity,
  unitCents: r.unit_cents,
  startDate: r.start_date,
  endDate: r.end_date,
  tvaRate: numericOrNull(r.tva_rate),
  personsTaxable: r.persons_taxable,
  personsExempt: r.persons_exempt,
  isManual: r.is_manual,
  voidedAt: r.voided_at,
});

const embeddedOrigin = (e: ChargeDayRow['pms_charge']): { origin?: ChargeOrigin } => {
  const one = Array.isArray(e) ? e[0] : e;
  return one?.origin ? { origin: one.origin as ChargeOrigin } : {};
};

export const rowToFolio = (r: FolioRow): Folio => ({
  id: r.id,
  reservationId: r.reservation_id,
  roomStayId: r.room_stay_id,
  labelFr: r.label_fr,
  payerGuestId: r.payer_guest_id,
  payerName: r.payer_name,
});

export const rowToOccupancySpan = (r: OccupancyRow): OccupancySpan => ({
  id: r.id,
  roomStayId: r.room_stay_id,
  startDate: r.start_date,
  endDate: r.end_date,
  adults: r.adults,
  children: r.children,
  infants: r.infants,
});

export const rowToOtaService = (r: OtaServiceRow): OtaService => ({
  id: r.id,
  roomStayId: r.room_stay_id,
  name: r.name,
  priceMode: r.price_mode,
  persons: r.persons,
  nights: r.nights,
  totalCents: r.total_cents,
  productId: r.product_id,
});

export const rowToChargeDay = (r: ChargeDayRow): ChargeDay => ({
  id: r.id,
  chargeId: r.charge_id,
  reservationId: r.reservation_id,
  roomStayId: r.room_stay_id,
  date: r.date,
  kind: r.kind as ChargeKind,
  productId: r.product_id,
  ratePlanId: r.rate_plan_id,
  labelFr: r.label_fr,
  quantity: r.quantity,
  unitCents: r.unit_cents,
  amountCents: r.amount_cents,
  tvaRate: numericOrNull(r.tva_rate),
  personsTaxable: r.persons_taxable,
  personsExempt: r.persons_exempt,
  isManual: r.is_manual,
  voidedAt: r.voided_at,
  ...embeddedOrigin(r.pms_charge),
});
